/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { useBreakpoint, Typography } from '@one-thd/sui-atomic-components';
import { SponsoredUniversalBanner } from '@thd-olt-component-react/sponsored-content';
import classnames from 'classnames';
import { BentoBay } from './sub-components/BentoBay';
import { BentoHeroCarousel } from './sub-components/BentoHeroCarousel';
import './bento-hero-container.scss';

const { shape, bool } = proptypes;
// Responsible for rendering the grid and which cells of the grid to show visually at each breakpoint.
// Breakdown (4 bay layout):
//   Mobile/Tablet breakpoint --
//     - 1st cell has 100% width (full width).
//     - 2nd cell has 50% width.
//     - 3rd cell has 50% width.
//     - 4th cell has 100% width.
//   Desktop breakpoint --
//     - 1st cell has ~66% width.
//     - 2nd cell has ~33% width.
//     - 3rd cell has 50% width.
//     - 4th cell has 50% width.
const BentoHeroContainer = ({ data, enableB2CRadius = false }) => {
  // A breakpoint cannot be both mobile and not mobile at the same time, and if they are the same (most likely false)
  // it means we have not fully loaded on the client yet. We want to wait until we have fully loaded on the client
  // otherwise we could be creating the wrong impression ID, clickID, (the ID will contain the wrong position for the
  // Bay in a 5 bay layout).
  const { lesser: isMobile } = useBreakpoint('lg', { ssr: true });
  const [impressionsAreReady, setImpressionsAreReady] = useState(false);
  const [firstBayPosition, setFirstBayPosition] = useState(1);
  const [secondBayPosition, setSecondBayPosition] = useState(2);

  useEffect(() => {
    if (!impressionsAreReady) {
      setImpressionsAreReady(true);
      setFirstBayPosition(isMobile ? 1 : 2);
      setSecondBayPosition(isMobile ? 2 : 1);
    }
  }, [isMobile]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bentoHero.ready');
  }, []);

  if (!data) {
    return null;
  }

  const {
    firstBay, secondBay, thirdBay, fourthBay, bentoSponsoredBanner, isLivePreviewData
  } = data;

  const { schema = null, specialDefaultBanner = {} } = bentoSponsoredBanner || {};

  let contentfulDefaultBanner = null;
  if (specialDefaultBanner.imgDesktop && specialDefaultBanner.imgMobile) {
    contentfulDefaultBanner = { specialDefaultBanner };
  }

  const fourBentoBayLayout = (
    <div className="sui-grid sui-grid-cols-12 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 sui-bg-primary">
      <div className="sui-col-span-12 lg:sui-col-span-8 sui-aspect-[1.43/1] md:sui-aspect-[3/1] lg:sui-aspect-[3.33/1] sui-h-full sui-w-full">
        <BentoBay
          bentoBayData={firstBay}
          position={1}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
      <div className="sui-col-span-6 lg:sui-col-span-4 sui-aspect-[1/1.47] md:sui-aspect-[1.45/1] lg:sui-aspect-[1.6/1] sui-h-full sui-w-full">
        {isLivePreviewData ? (
          <div className="sui-flex sui-items-center sui-bg-strong sui-justify-center sui-w-full sui-py-4">
            <Typography>SponsoredBanner Placeholder</Typography>
          </div>
        ) : (
          <SponsoredUniversalBanner
            bentoBayData={bentoSponsoredBanner}
            contentfulDefaultBanner={contentfulDefaultBanner}
            impressionReady={impressionsAreReady}
            position={2}
            schema={schema}
            renderDefaultBanner
            bannerType="standard"

          />
        )}
      </div>
      <div className="sui-col-span-6 sui-aspect-[1/1.47] md:sui-aspect-[1.45/1] lg:sui-aspect-[4.93/1] sui-h-full sui-w-full">
        <BentoBay
          bentoBayData={secondBay}
          position={3}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
      <div className="sui-col-span-12 lg:sui-col-span-6 sui-aspect-[2.86/1] md:sui-aspect-[6/1] lg:sui-aspect-[4.93/1] sui-h-full sui-w-full">
        <BentoBay
          bentoBayData={thirdBay}
          position={4}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
    </div>
  );

  const fiveBentoBayLayout = (
    <div className="sui-grid sui-grid-flow-row sui-grid-cols-12 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 sui-bg-primary">
      {firstBay?.isCarousel ? (
        <BentoHeroCarousel
          carouselData={firstBay?.components}
          position={firstBayPosition}
          isAutoplay={firstBay?.isAutoplay}
          enableB2CRadius={enableB2CRadius}
        />
      ) : (
        <div className={classnames('sui-col-span-12 lg:sui-col-span-8 sui-order-1 lg:sui-order-2 sui-w-full', {
          'bento-clip-path-rounded': enableB2CRadius,
        })}
        >
          <BentoBay
            bentoBayData={firstBay}
            position={firstBayPosition}
            impressionReady={impressionsAreReady}
            isSponsored
            enableB2CRadius={enableB2CRadius}
          />
        </div>
      )}
      {/* Sponsored Content */}
      <div
        className={classnames(
          'bento-hero-sponsored-content-wrapper',
          'sui-col-span-12 lg:sui-col-span-12 sui-order-2 lg:sui-order-3 lg:sui-cols-4 sui-w-full',
          {
            'bento-clip-path-rounded': enableB2CRadius,
          }
        )}
      >
        {isLivePreviewData ? (
          <div className="sui-flex sui-items-center sui-bg-strong sui-justify-center sui-w-full sui-py-4">
            <Typography>SponsoredBanner Placeholder</Typography>
          </div>
        ) : (
          <SponsoredUniversalBanner
            bentoBayData={bentoSponsoredBanner}
            contentfulDefaultBanner={contentfulDefaultBanner}
            data-testid="sponsored-banner"
            impressionReady={impressionsAreReady}
            position={secondBayPosition}
            schema={schema}
            renderDefaultBanner
            bannerType="standard"
          />
        )}
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-4 sui-order-3 lg:sui-order-2 lg:sui-aspect-[1.8/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        <BentoBay
          bentoBayData={secondBay}
          position={secondBayPosition}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-8 sui-order-4 lg:sui-order-5 lg:sui-aspect-[3.6/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        <BentoBay
          bentoBayData={thirdBay}
          position={3}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
      <div className={classnames('sui-col-span-12 lg:sui-col-span-4 sui-order-5 lg:sui-order-4 lg:sui-aspect-[1.8/1] sui-w-full sui-h-full', {
        'bento-clip-path-rounded': enableB2CRadius,
      })}
      >
        <BentoBay
          bentoBayData={fourthBay}
          position={4}
          impressionReady={impressionsAreReady}
          enableB2CRadius={enableB2CRadius}
        />
      </div>
    </div>
  );

  return (
    <>
      {fourthBay ? fiveBentoBayLayout : fourBentoBayLayout}
    </>
  );
};

BentoHeroContainer.propTypes = {
  data: shape({
    firstBay: shape({}),
    secondBay: shape({}),
    thirdBay: shape({}),
    fourthBay: shape({}),
    bentoSponsoredBanner: shape({}),
  }).isRequired,
  enableB2CRadius: bool
};

export { BentoHeroContainer };
